import React, { useState, useEffect } from 'react';
import { Palette, Settings as SettingsIcon, Globe, Plus, CheckCircle, AlertCircle, Trash2, ExternalLink, FileSignature } from 'lucide-react';
import TemplatesView from '../features/templates/TemplatesView';
import { CRMUser, UserRole, authedFetch } from '../lib/db';
import { DEFAULT_STATUS_COLORS } from '../types';
import { auth } from '../lib/firebase';

interface SettingsProps {
  statusColors: Record<string, string>;
  onSaveStatusColors: (newColors: Record<string, string>) => void;
  users: CRMUser[];
  onAddUser: (email: string, name: string, role: UserRole) => Promise<string | void>;
  onUpdateRole: (uid: string, role: UserRole) => Promise<void>;
  onDeleteUser?: (uid: string) => Promise<void>;
  accessToken: string | null;
}

const DEFAULT_COLORS: Record<string, string> = { ...DEFAULT_STATUS_COLORS };

const PRESET_SWATCHES = ['#00c875', '#fdab3d', '#e2445c', '#0086f0', '#a25ddc', '#6366f1', '#cbd5e1', '#334155'];

export default function Settings({
  statusColors,
  onSaveStatusColors,
  users,
  onAddUser,
  onUpdateRole,
  onDeleteUser,
  accessToken,
}: SettingsProps) {
  const [activeSection, setActiveSection] = useState<'colors' | 'form' | 'industries' | 'templates'>('colors');

  // Dynamic Industry Options Management
  const [industriesList, setIndustriesList] = useState<string[]>([]);
  const [newIndustryName, setNewIndustryName] = useState('');
  const [industriesLoading, setIndustriesLoading] = useState(false);
  const [industriesError, setIndustriesError] = useState<string | null>(null);

  const fetchIndustries = async () => {
    setIndustriesLoading(true);
    setIndustriesError(null);
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await fetch('/api/industries', {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      if (!res.ok) throw new Error('Failed to load industries');
      const data = await res.json();
      setIndustriesList(data);
    } catch (err: any) {
      setIndustriesError(err.message || 'Error loading industries');
    } finally {
      setIndustriesLoading(false);
    }
  };

  useEffect(() => {
    if (activeSection === 'industries') {
      fetchIndustries();
    }
  }, [activeSection]);

  const handleAddIndustry = async () => {
    const name = newIndustryName.trim();
    if (!name) return;
    if (industriesList.some(i => i.toLowerCase() === name.toLowerCase())) {
      alert(`Industry "${name}" already exists.`);
      return;
    }
    try {
      const res = await authedFetch('/api/industries', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ name })
      });
      if (!res.ok) throw new Error('Failed to add industry');
      setNewIndustryName('');
      fetchIndustries();
    } catch (err: any) {
      alert(err.message || 'Error adding industry');
    }
  };

  const handleDeleteIndustry = async (name: string) => {
    if (!window.confirm(`Are you sure you want to delete the industry "${name}"? Existing leads will keep their selected industry, but it will no longer be an option for new submissions.`)) return;
    try {
      const res = await authedFetch(`/api/industries/${encodeURIComponent(name)}`, {
        method: 'DELETE',
      });
      if (!res.ok) throw new Error('Failed to delete industry');
      fetchIndustries();
    } catch (err: any) {
      alert(err.message || 'Error deleting industry');
    }
  };

  // Public lead capture form link + embed helpers
  const formUrl = `${window.location.origin}/lead-form`;
  const embedCode = `<iframe src="${formUrl}" width="100%" height="900" style="border:0;max-width:640px" title="Armellini CRM Lead Form"></iframe>`;
  const [copied, setCopied] = useState<string | null>(null);
  const copy = (text: string, which: string) => {
    navigator.clipboard?.writeText(text).then(() => {
      setCopied(which);
      setTimeout(() => setCopied(null), 2000);
    });
  };

  // Status badge CRUD
  const [newStatusName, setNewStatusName] = useState('');
  const [newStatusColor, setNewStatusColor] = useState('#579bfc');
  const statusList = Object.keys(statusColors);

  const handleAddStatus = () => {
    const name = newStatusName.trim();
    if (!name) return;
    if (statusList.some(s => s.toLowerCase() === name.toLowerCase())) {
      alert(`Status "${name}" already exists.`);
      return;
    }
    onSaveStatusColors({ ...statusColors, [name]: newStatusColor });
    setNewStatusName('');
  };

  const handleDeleteStatus = (name: string) => {
    if (!window.confirm(`Delete the status "${name}"? Leads currently using it keep their label but lose the custom badge color, and it disappears from the status dropdowns.`)) return;
    const next = { ...statusColors };
    delete next[name];
    onSaveStatusColors(next);
  };

  return (
    <div className="flex-1 overflow-y-auto p-4 md:p-8 bg-slate-100 dark:bg-slate-900 transition-colors">
      <div className="max-w-5xl mx-auto">

        <div className="mb-6 border-b border-slate-200 dark:border-slate-800 pb-5">
          <h1 className="text-2xl font-extrabold tracking-tight text-black dark:text-white flex items-center gap-2">
            <SettingsIcon className="w-6 h-6 text-blue-600" /> System Maintenance
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1 flex items-center gap-1.5">
            System configuration, customization, and template maintenance tools.
          </p>
        </div>

        {/* Section tabs */}
        <div className="flex gap-1 mb-6 p-1 bg-slate-200/60 dark:bg-slate-950 rounded-lg w-fit border border-slate-200 dark:border-slate-800">
          <button
            onClick={() => setActiveSection('colors')}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-md text-xs font-bold transition cursor-pointer ${
              activeSection === 'colors'
                ? 'bg-white dark:bg-slate-900 text-slate-900 dark:text-white shadow-sm'
                : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
            }`}
          >
            <Palette className="w-3.5 h-3.5" /> Status Colors
          </button>
          <button
            onClick={() => setActiveSection('form')}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-md text-xs font-bold transition cursor-pointer ${
              activeSection === 'form'
                ? 'bg-white dark:bg-slate-900 text-slate-900 dark:text-white shadow-sm'
                : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
            }`}
          >
            <Globe className="w-3.5 h-3.5" /> Lead Capture Form
          </button>
          <button
            onClick={() => setActiveSection('industries')}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-md text-xs font-bold transition cursor-pointer ${
              activeSection === 'industries'
                ? 'bg-white dark:bg-slate-900 text-slate-900 dark:text-white shadow-sm'
                : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
            }`}
          >
            <SettingsIcon className="w-3.5 h-3.5" /> Manage Industries
          </button>
          <button
            onClick={() => setActiveSection('templates')}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-md text-xs font-bold transition cursor-pointer ${
              activeSection === 'templates'
                ? 'bg-white dark:bg-slate-900 text-slate-900 dark:text-white shadow-sm'
                : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
            }`}
          >
            <FileSignature className="w-3.5 h-3.5" /> Template Management
          </button>
        </div>

        {/* STATUS COLORS SECTION */}
        {activeSection === 'colors' && (
          <section className="bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-sm space-y-6">
            <div className="flex items-center justify-between border-b border-slate-100 dark:border-slate-800 pb-4">
              <div>
                <h2 className="text-sm font-extrabold text-black dark:text-slate-100 uppercase tracking-wider flex items-center gap-2">
                  <Palette className="w-4 h-4 text-blue-600" /> Pipeline & Status Badge Colors
                </h2>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                  Customize the badge color for each status label across the board, list view, and slide-overs.
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  if (window.confirm('Reset all status colors to factory defaults?')) {
                    onSaveStatusColors(DEFAULT_COLORS);
                  }
                }}
                className="text-xs font-bold text-slate-500 hover:text-slate-800 dark:hover:text-slate-200 underline cursor-pointer"
              >
                Restore Defaults
              </button>
            </div>

            {/* Add custom status */}
            <div className="p-4 rounded-xl border border-dashed border-blue-200 dark:border-blue-900 bg-blue-50/40 dark:bg-blue-950/10 flex flex-col sm:flex-row items-stretch sm:items-end gap-3">
              <div className="flex-1">
                <label className="block text-[11px] font-bold text-slate-500 uppercase mb-1">New Status Label</label>
                <input
                  type="text"
                  value={newStatusName}
                  onChange={(e) => setNewStatusName(e.target.value)}
                  placeholder="e.g., Credit App Approved"
                  className="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded px-3 py-2 text-sm text-slate-800 dark:text-slate-100 outline-none focus:border-blue-500"
                />
              </div>

              <div className="shrink-0">
                <label className="block text-[11px] font-bold text-slate-500 uppercase mb-1">Badge Color</label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={newStatusColor}
                    onChange={(e) => setNewStatusColor(e.target.value)}
                    className="w-10 h-9 rounded cursor-pointer border border-slate-300 dark:border-slate-700 p-0.5 bg-white"
                  />
                  <span className="font-mono text-xs font-bold text-slate-600 dark:text-slate-300">{newStatusColor}</span>
                </div>
              </div>

              <button
                type="button"
                onClick={handleAddStatus}
                disabled={!newStatusName.trim()}
                className="bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs px-4 py-2.5 rounded-lg transition cursor-pointer disabled:opacity-50 shrink-0"
              >
                + Add Status Badge
              </button>
            </div>

            {/* Preset Palette Swatches */}
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold text-slate-400">Quick Swatches:</span>
              <div className="flex items-center gap-1.5">
                {PRESET_SWATCHES.map((hex) => (
                  <button
                    key={hex}
                    type="button"
                    onClick={() => setNewStatusColor(hex)}
                    className="w-5 h-5 rounded-full border border-slate-300 dark:border-slate-700 transition hover:scale-110 cursor-pointer"
                    style={{ backgroundColor: hex }}
                    title={`Set color to ${hex}`}
                  />
                ))}
              </div>
            </div>

            {/* Statuses Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2">
              {statusList.map((name) => {
                const color = statusColors[name] || '#579bfc';
                return (
                  <div
                    key={name}
                    className="flex items-center justify-between p-3 rounded-xl border border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/30"
                  >
                    <div className="flex items-center gap-3">
                      <input
                        type="color"
                        value={color}
                        onChange={(e) => onSaveStatusColors({ ...statusColors, [name]: e.target.value })}
                        className="w-7 h-7 rounded cursor-pointer border border-slate-200 dark:border-slate-700 bg-transparent p-0"
                        title={`Change color for "${name}"`}
                      />
                      <span
                        className="inline-block px-2.5 py-1 rounded text-xs font-extrabold uppercase tracking-wider text-white shadow-xs"
                        style={{ backgroundColor: color.startsWith('#') ? color : `#${color}` }}
                      >
                        {name}
                      </span>
                    </div>

                    <button
                      type="button"
                      onClick={() => handleDeleteStatus(name)}
                      className="text-slate-400 hover:text-red-600 transition p-1 cursor-pointer"
                      title={`Delete "${name}" status color`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* LEAD CAPTURE FORM SECTION */}
        {activeSection === 'form' && (
          <section className="bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-sm space-y-6">
            <div className="border-b border-slate-100 dark:border-slate-800 pb-4">
              <h2 className="text-sm font-extrabold text-black dark:text-slate-100 uppercase tracking-wider flex items-center gap-2">
                <Globe className="w-4 h-4 text-blue-600" /> Public Lead Capture Form
              </h2>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                Share this direct URL with prospects or embed the form on your corporate website. Submissions land instantly on your sales pipeline board.
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1">
                  Standalone Form URL
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    readOnly
                    value={formUrl}
                    className="flex-1 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg px-3 py-2 text-xs font-mono text-slate-800 dark:text-slate-200 outline-none"
                  />
                  <button
                    type="button"
                    onClick={() => copy(formUrl, 'url')}
                    className="bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs px-3.5 py-2 rounded-lg transition cursor-pointer shrink-0"
                  >
                    {copied === 'url' ? 'Copied!' : 'Copy Link'}
                  </button>
                  <a
                    href="/lead-form"
                    target="_blank"
                    rel="noreferrer"
                    className="bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 font-bold text-xs px-3.5 py-2 rounded-lg transition flex items-center gap-1 cursor-pointer shrink-0"
                  >
                    Open Form <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1">
                  iFrame Embed Code (for website integration)
                </label>
                <div className="flex items-start gap-2">
                  <textarea
                    readOnly
                    rows={3}
                    value={embedCode}
                    className="flex-1 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg p-3 text-xs font-mono text-slate-800 dark:text-slate-200 outline-none"
                  />
                  <button
                    type="button"
                    onClick={() => copy(embedCode, 'embed')}
                    className="bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs px-3.5 py-2 rounded-lg transition cursor-pointer shrink-0"
                  >
                    {copied === 'embed' ? 'Copied!' : 'Copy Code'}
                  </button>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* MANAGE INDUSTRIES SECTION */}
        {activeSection === 'industries' && (
          <section className="bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-sm">
            <div className="flex items-center justify-between border-b border-slate-100 dark:border-slate-800 pb-4 mb-4">
              <div>
                <h2 className="text-sm font-bold text-blue-600 dark:text-blue-400 uppercase tracking-widest flex items-center gap-1.5">
                  <SettingsIcon className="w-4 h-4" /> Manage Industry Options
                </h2>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                  Add, remove, and manage the list of industry selections available on the public lead capture form.
                </p>
              </div>
              {industriesLoading && <span className="text-xs text-blue-600">Loading...</span>}
            </div>

            {industriesError && (
              <div className="p-3 rounded text-xs flex items-center gap-2 bg-red-50 dark:bg-red-950/25 text-red-800 dark:text-red-300 mb-3">
                <AlertCircle className="w-4 h-4 shrink-0" />
                <span>{industriesError}</span>
              </div>
            )}

            {/* Add a new industry */}
            <div className="mb-5 p-4 rounded-xl border border-dashed border-blue-200 dark:border-blue-900 bg-blue-50/40 dark:bg-blue-950/10 flex flex-col sm:flex-row items-stretch sm:items-end gap-3">
              <div className="flex-1">
                <label className="block text-[11px] font-bold text-slate-500 uppercase mb-1">New Industry Name</label>
                <input
                  type="text"
                  value={newIndustryName}
                  onChange={(e) => setNewIndustryName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleAddIndustry()}
                  placeholder="e.g. Nursery, Retail"
                  className="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded px-3 py-2 text-sm text-slate-800 dark:text-slate-100 outline-none focus:border-blue-500"
                />
              </div>
              <button
                type="button"
                onClick={handleAddIndustry}
                disabled={!newIndustryName.trim()}
                className="bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs px-4 py-2.5 rounded-lg transition cursor-pointer disabled:opacity-50 shrink-0"
              >
                + Add Option
              </button>
            </div>

            <div className="space-y-2 max-w-xl">
              {industriesList.map((name) => (
                <div
                  key={name}
                  className="flex items-center justify-between gap-3 p-3 rounded-lg border border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/20"
                >
                  <span className="text-xs font-bold text-slate-700 dark:text-slate-200">{name}</span>
                  <button
                    type="button"
                    onClick={() => handleDeleteIndustry(name)}
                    className="text-slate-400 hover:text-red-600 transition cursor-pointer p-1"
                    title={`Delete "${name}"`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
              {industriesList.length === 0 && !industriesLoading && (
                <p className="text-xs text-slate-400 py-4 text-center">No industry options configured. Standard defaults are active.</p>
              )}
            </div>
          </section>
        )}

        {/* TEMPLATE MANAGEMENT SECTION */}
        {activeSection === 'templates' && (
          <section className="bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-sm">
            <TemplatesView
              user={auth.currentUser}
              teamUsers={users}
              showToast={(msg: string, type?: string) => {
                if (type === 'error') alert(`Error: ${msg}`);
                else console.log(`[Toast] ${msg}`);
              }}
            />
          </section>
        )}

      </div>
    </div>
  );
}
